// Mirror of backend-crm/src/modules/crm/services/customer-list.types.ts
// Do NOT cross-worktree-import — duplicate the shape here.

/**
 * Product classification derived solely from user.userType:
 *   - 'MarketingOnly' → 'marketing'  (SEM-only customers)
 *   - 'U'             → 'all_in_one' (SEM + pricing)
 * Admins ('A') are filtered out server-side and never appear.
 */
export type ProductType = "marketing" | "all_in_one";

export type BillingStatusPill =
  | "ok"
  | "card_expired"
  | "charge_failed"
  | "past_due"
  | "unknown";

export type ChurnRiskLevel = "green" | "yellow" | "red";

export type ProductCapsule =
  | "sem"
  | "website"
  | "email"
  | "social"
  | "pricing";

export type ProductCapsulesSource = "ai" | "manual";

export const PRODUCT_CAPSULE_LABELS: Record<ProductCapsule, string> = {
  sem: "SEM",
  website: "Website",
  email: "Email",
  social: "Social",
  pricing: "Pricing",
};

export interface OwnerRef {
  ownershipId: string;
  adminId: string;
  adminEmail: string;
  adminFirstName: string | null;
}

export interface CustomerRow {
  id: string;
  hotelName: string | null;
  primaryEmail: string;
  productType: ProductType;
  createdAt: string; // ISO string on the wire (Date on backend)
  isTestAccount: boolean;
  stripeCustomerId: string | null;
  productCapsules: ProductCapsule[];
  productCapsulesSource: ProductCapsulesSource | null;
  productCapsulesUpdatedAt: string | null;
  pms: string | null;
  owners: OwnerRef[];

  contract: {
    tier: string | null;
    services: "both" | "marketing" | "pricing" | null;
    commissionRate: number | null;
    termMonths: number | null;
    signedDate: string | null; // ISO string
    endDate: string | null; // ISO string — column-spec-revised addition
    cancellationClause: string | null;
    hasContractRow: boolean;
  };

  billing: {
    totalCommissionYtd: number | null;
    latestChargeStatus: string | null;
    latestChargedAt: string | null;
    chargeCountAllTime: number;
    failedChargeCount: number;
    // Phase 2 Stage 5 — real fields (Phase-1 subscriptionAmount/status placeholders dropped)
    lastCommissionBill: number | null;
    fixedFeeMonthly: number | null;
    monthlyRevenue: number | null;
    /** Billing arrangement — commission / fixed / hybrid / commission_or_flat. */
    billingType:
      | "fixed"
      | "commission"
      | "hybrid"
      | "commission_or_flat"
      | null;
    /**
     * Contractual monthly floor for a commission_or_flat client, else null.
     * Already floors monthlyRevenue / acvArr* / carr server-side — do not
     * re-apply it here.
     */
    minimumFeeAmount: number | null;
    paymentMethodStatus: string | null;
    commissionRate: number | null;
    acvArrDefault: number | null;
    acvArrAvg3: number | null;
    /**
     * Resolved CARR (Contracted ARR) in dollars. Manual override > acvArrDefault
     * (= MRR × 12 when revenue exists) > $6,000 placeholder.
     */
    carr: number | null;
    /** True when CARR comes from a manual override, false when computed. */
    carrIsManual: boolean;
    /** True when CARR is the $6k placeholder (no override, no revenue data). */
    carrIsPlaceholder: boolean;
    /** True when MRR / ACV-ARR / CARR are all the $500/$6,000 placeholder triplet. */
    revenueIsPlaceholder: boolean;
    planName: string | null;
    planDescription: string | null;
    // Column-spec-revised additions
    tenureMonths: number;
    daysToRenewal: number | null;
    billingStatus: BillingStatusPill;
  };

  marketing: {
    bookings30d: number | null;
    bookingsValueUsd30d: number | null;
    impressions30d: number | null;
    /** Total ad impressions over the last 7 days (rolling window). */
    impressions7d: number | null;
    /**
     * Week-over-week impressions trend: (last 7d - prior 7d) / prior 7d,
     * signed fraction (0.15 = +15%). Null when the prior window is 0 or
     * the snapshot couldn't fetch a window.
     */
    impressionsWowDelta: number | null;
    clicks30d: number | null;
    spend30d: number | null;
    roas30d: number | null;
    autumnShareOfTraffic: number | null;
    autumnShareOfBookings: number | null;
    bookingsTrendDelta: number | null;
    revenue30d: number | null;
    margin30d: number | null;
    occupancyLift30d: number | null;
    adrChange30d: number | null;
    revParImprovement30d: number | null;
    // Column-spec-revised additions
    ctr: number | null;
    cac: number | null;
    spendPacing: number | null;
    /**
     * Share of 30d bookings attributed to Brand Protection campaigns
     * (Google Ads campaigns protecting the hotel's brand name). Decimal
     * fraction 0–1. Null when the snapshot lacks a campaign breakdown,
     * bookings30d is null/zero, or no brandProtection category is present.
     */
    brandProtectionPct: number | null;
    /**
     * Raw count of 30d bookings from Brand Protection campaigns. Pairs
     * with `brandProtectionPct` so CSMs can read magnitude alongside
     * the ratio (1-of-1 vs 10-of-10 are very different signals).
     */
    brandProtectionBookings30d: number | null;
    /** Raw count of 30d bookings NOT from Brand Protection campaigns. */
    nonBrandProtectionBookings30d: number | null;
  };

  pricing: {
    daysOnPricing: number | null;
    revParYoY: number | null;
    adrYoY: number | null;
    occupancyYoY: number | null;
    revParSinceGoLive: number | null;
    revenueLiftSinceGoLiveUsd: number | null;
  };

  activity: {
    lastActive: string | null; // ISO string
    daysSinceLastEmail: number | null;
    dashboardUnlockedAt: string | null; // ISO string
    shouldPushPricesEnabledAt: string | null; // ISO string
  };

  workload: {
    openTicketCount: number;
    /** Lifetime / all-time CS ticket count for this customer. */
    totalTicketCount: number;
    /**
     * Phase 4 Stage 4 — count of Gmail threads where the latest email-like
     * event is inbound with no outbound event after it. Self-clears when
     * ComposeEmailModal sends a reply on the same thread.
     */
    needsReplyCount: number;
    ticketsThisMonth: number;
  };

  engagement: {
    lastProactiveTouchAt: string | null; // ISO string
    inAppRating: number | null;
  };

  nextAction: {
    body: string | null;
    dueDate: string | null; // ISO string
  };

  note: {
    id: string;
    body: string;
    authorId: string;
    createdAt: string; // ISO string
    totalCount: number;
  } | null;

  churnRisk: {
    level: ChurnRiskLevel;
    triggers: string[];
    isManual: boolean;
  };

  marginTrend90d: {
    slope: number;
    dataPoints: number[];
  } | null;

  health: {
    score: number | null; // null only until first health-score row is computed
    band: HealthBand | null; // null until Stage 5 populates via customer_health_score
    componentBreakdown?: HealthComponentBreakdown | null;
    segment?: CustomerSegment;
  };
}

/**
 * 4-band classification — "insufficient_data" was retired per directive
 * 2026-04-26. The backend now always produces a score by aggressively
 * rescaling whatever signals are available, falling back to healthy/75 only
 * when zero signals are present AND no negative signals exist.
 */
export type HealthBand = "healthy" | "watch" | "concerning" | "at_risk";

export type CustomerSegment = "ramp_up" | "stabilized";

export interface HealthSignalBreakdown {
  value: number | null;
  weight: number;
  available: boolean;
  rationale: string;
}

export interface HealthComponentBreakdown {
  sentiment: HealthSignalBreakdown;
  productPerf: HealthSignalBreakdown;
  inAppRating: HealthSignalBreakdown;
  billing: HealthSignalBreakdown;
  engagement: HealthSignalBreakdown;
  onboarding: HealthSignalBreakdown;
}

export interface CustomerListResponse {
  rows: CustomerRow[];
  fetchedAt: string; // ISO timestamp
}

// ---------------------------------------------------------------------------
// Campaign category breakdown — shape mirrors googleAds dashboard response
// (stored as JSONB in marketing_metrics_snapshot.campaignCategoryBreakdown)
// ---------------------------------------------------------------------------

export interface CampaignRow {
  campaignId: number;
  campaignName: string;
  impressions: number;
  clicks: number;
  /** Already expressed as a percentage (e.g., 28.76 means 28.76%). */
  ctr: number;
  atc: number;
  bco: number;
  pur: number;
  purchaseValue: number;
  /** Already expressed as a percentage (e.g., 3.62 means 3.62%). */
  cvr: number;
}

export interface CampaignCategory {
  key: string;
  label: string;
  impressions: number;
  clicks: number;
  ctr: number;
  atc: number;
  bco: number;
  pur: number;
  purchaseValue: number;
  cvr: number;
  campaigns: CampaignRow[];
}

export interface CampaignCategoryBreakdown {
  categories: CampaignCategory[];
  totalBookings: number;
}

// ---------------------------------------------------------------------------
// Customer 360 — mirrors backend-crm/.../customer-360.service.ts
// ---------------------------------------------------------------------------

/** Mirror of Customer360Snapshot from the backend service. */
export interface Customer360Snapshot {
  customer: {
    id: string;
    hotelName: string | null;
    primaryEmail: string;
    additionalEmails: string[];
    aliases: Array<{
      id: string;
      email: string;
      isPrimary: boolean;
      source: string | null;
    }>;
    dashboardUnlockedAt: string | null;
    shouldPushPricesEnabledAt: string | null;
    /** Phase 2 — derived from user.userType (MarketingOnly vs all_in_one). */
    productType: "marketing" | "all_in_one";
    /** Whether the customer's hotel has the email-marketing product enabled. */
    emailMarketingEnabled: boolean;
    /** Whether the customer's hotel has the Website Builder app enabled. */
    websiteBuilderEnabled?: boolean;
    /** Pre-population data for the Enable Email Marketing modal. */
    emailMarketing: EmailMarketingProfile;
  };
  contract: {
    services: string | null;
    termMonths: number | null;
    signedDate: string | null;
    commissionRate: number | null;
    tier: string | null;
    cancellationClause: string | null;
    billingStatus: string | null;
    stripePortalUrl: string | null;
    planName: string | null;
    planDescription: string | null;
    /**
     * Billing arrangement from client_billing_config — commission / fixed /
     * hybrid / commission_or_flat. Distinct from planName, which is the Stripe
     * product name.
     */
    billingType: "fixed" | "commission" | "hybrid" | "commission_or_flat" | null;
    /** Contractual monthly floor (USD). Recorded only — not charged yet. */
    minimumFeeAmount: number | null;
    fixedFeeMonthly: number | null;
    lastCommissionBill: number | null;
    totalCommissionYtd: number | null;
    latestChargeStatus: string | null;
    latestChargedAt: string | null;
    chargeCountAllTime: number;
    monthlyRevenue: number | null;
    paymentMethodStatus: string | null;
    paymentMethodBrand: string | null;
    paymentMethodLast4: string | null;
    paymentMethodExpiry: string | null;
  };
  openTicketCount: number;
  lastInteractionAt: string | null;
  /**
   * Most-recent AI summary row for this customer. Read-only mirror of the
   * /admin/customers/:id/ai-summary endpoint's latest value — populated so
   * Ask Autumn and any other 360 consumers can access the distilled
   * narrative without a second round-trip. Null when the customer has no
   * summary yet.
   */
  aiSummary: {
    summaryText: string | null;
    status: "fresh" | "stale" | "generating" | "error";
    model: string | null;
    computedAt: string | null;
  } | null;
  // Phase 2 Stage 5 populates
  healthScore: {
    score: number | null;
    band: HealthBand | null;
    segment: CustomerSegment | null;
    componentBreakdown: HealthComponentBreakdown | null;
    computedAt: string | null;
  } | null;
  marketing: {
    bookings30d: number | null;
    bookingsPrev30d: number | null;
    impressions30d: number | null;
    clicks30d: number | null;
    spend30d: number | null;
    bookingsValueUsd30d: number | null;
    roas30d: number | null;
    autumnShareOfTraffic: number | null;
    autumnShareOfBookings: number | null;
    bookingsTrendDelta: number | null;
    revenue30d: number | null;
    margin30d: number | null;
    occupancyLift30d: number | null;
    adrChange30d: number | null;
    revParImprovement30d: number | null;
    commissionRate: number | null;
    campaignCategoryBreakdown: CampaignCategoryBreakdown | null;
  } | null;
  feedbackHistory: Array<{
    id: string;
    rating: number;
    comment: string | null;
    page: string | null;
    submittedAt: string;
  }>;
}

// ---------------------------------------------------------------------------
// Communication Events — mirrors communication-event.entity.ts + types.ts
// ---------------------------------------------------------------------------

export type CommunicationSource =
  | "email_inbound"
  | "email_outbound"
  | "slack"
  | "fireflies"
  | "granola"
  | "posthog"
  | "stripe"
  | "feedback"
  | "note"
  | "cs_ticket";

export interface CommunicationEvent {
  id: string;
  userId: string;
  source: CommunicationSource;
  type: string;
  externalId: string;
  occurredAt: string; // ISO string
  summary: string;
  payloadRef: string | null;
  payload: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  searchableText: string | null;
  indexedInRag: boolean;
}

export interface TimelinePage {
  events: CommunicationEvent[];
  limit: number;
  offset: number;
}

// ---------------------------------------------------------------------------
// Contract update DTO — mirrors backend UpdateContractDto
// signedDate intentionally excluded (server-protected).
// services/tier/termMonths removed per user directive 2026-04-23 — those
// concepts aren't used in the current billing model.
// commissionRate writes to client_billing_config on the backend (source of
// truth), not customer_contract.
// ---------------------------------------------------------------------------

export interface UpdateContractDto {
  commissionRate?: number | null;
  cancellationClause?: string | null;
}

// ---------------------------------------------------------------------------
// Email-marketing enable — mirrors backend UpdateEmailMarketingDto.
// For marketing-only customers the backend INSERTs the hotels row from these
// fields; for all-in-one customers it UPDATEs the existing row. The CB/Mews
// credentials are only collected when there's no active user_pms row and are
// routed to autumn-email's pms_connections (never user_pms).
// ---------------------------------------------------------------------------

export type EmailMarketingPmsType = "cloudbeds" | "mews";

/** Hotels-row fields the admin form confirms/fills before enabling. */
export interface EmailMarketingHotelFields {
  name: string | null;
  timezone: string | null;
  defaultLocale: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string | null;
  latitude: number | null;
  longitude: number | null;
  currencyCode: string | null;
  currencySymbol: string | null;
  pmsId: string | null;
}

export interface EmailMarketingProfile {
  hasHotelRow: boolean;
  hasActivePms: boolean;
  pmsType: string | null;
  hotel: EmailMarketingHotelFields;
}

export interface UpdateEmailMarketingDto {
  emailMarketingEnabled: boolean;
  // Hotel-row fields (all optional — omit to leave existing values untouched).
  name?: string;
  timezone?: string;
  defaultLocale?: string;
  website?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  currencyCode?: string;
  currencySymbol?: string;
  pmsId?: string;
  // Marketing-only Cloudbeds/Mews credentials.
  pmsType?: EmailMarketingPmsType;
  pmsApiKey?: string;
  pmsPropertyId?: string;
}
